
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Account, Connector, ConnectorDetails, PublicationType, PostDocument, ApiBlock, PublishPostRequest, PostTarget } from "../types";
import { getConnectors, getAccounts, getConnectorDetails, sendPost } from "../services/api";
import { mergeManifests, getCompliantBlocks, getAllowedBlocksForType } from "../utils/manifestUtils";
import { UniversalEditor, ValidationIssue } from "./editor/UniversalEditor";
import { stripMarkdown } from "../utils/markdownUtils";
import { markdownToTelegramHtml } from "../utils/markdownConverter";
import { 
    X, CheckCircle, ArrowRight, Loader2, AlertCircle, 
    FileImage, Film, Newspaper, Check, Filter, Users, ShieldCheck, CheckSquare, Square, AlertTriangle,
    ChevronDown, Clock, Send, Calendar as CalendarIcon, Info
} from "lucide-react";

interface PostWizardProps {
    onClose: () => void;
    onSuccess: () => void;
}

type Step = 'type_selection' | 'account_selection' | 'editor' | 'publishing' | 'moderation_success';

export const PostWizard: React.FC<PostWizardProps> = ({ onClose, onSuccess }) => {
    // Workflow State
    const [step, setStep] = useState<Step>('type_selection');
    
    // Loading States
    const [initialLoading, setInitialLoading] = useState(true);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    
    const [error, setError] = useState<string | null>(null);
    const [scheduleError, setScheduleError] = useState<string | null>(null);
    const [loadingMessage, setLoadingMessage] = useState<string>("");

    // Data State
    const [connectors, setConnectors] = useState<Connector[]>([]);
    const [accountsMap, setAccountsMap] = useState<Record<string, Account[]>>({});
    const [allManifests, setAllManifests] = useState<Record<string, ConnectorDetails>>({});
    
    // Selection State
    const [selectedType, setSelectedType] = useState<PublicationType | null>(null);
    const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set());
    const [activeConnectorId, setActiveConnectorId] = useState<string | null>(null);
    
    // Scheduling State
    const [scheduledAt, setScheduledAt] = useState<string | null>(null);
    const [isPublishDropdownOpen, setIsPublishDropdownOpen] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleInputValue, setScheduleInputValue] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Manifests & Editor State
    const [loadedManifests, setLoadedManifests] = useState<ConnectorDetails[]>([]);
    const [commonManifest, setCommonManifest] = useState<any>(null);
    
    const [postDoc, setPostDoc] = useState<PostDocument>({
        id: "",
        publicationType: 'feed',
        blocks: [],
        networkSettings: {}
    });

    // Validation State passed up from Editor
    const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
    const [showValidationPopover, setShowValidationPopover] = useState(false);

    useEffect(() => {
        const loadMetadata = async () => {
            setInitialLoading(true);
            try {
                const connRes = await getConnectors();
                setConnectors(connRes.result);
                const manifests: Record<string, ConnectorDetails> = {};
                await Promise.all(connRes.result.map(async (c) => {
                    try {
                        const details = await getConnectorDetails(c.id);
                        manifests[c.id] = details.result;
                    } catch (e) { console.warn(`Could not fetch manifest for ${c.id}`, e); }
                }));
                setAllManifests(manifests);
            } catch (e) {
                setError("Не удалось загрузить данные коннекторов");
            } finally {
                setInitialLoading(false);
            }
        };
        loadMetadata();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsPublishDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const finalValidationIssues = useMemo(() => {
        const issues = [...validationIssues];
        
        postDoc.blocks.forEach(block => {
            if (block.type === 'text') {
                const text = (block.content?.text || "").trim();
                if (!text) {
                    issues.push({
                        network: "Контент",
                        message: "Текстовый блок не может быть пустым. Введите сообщение или удалите блок.",
                        type: 'error'
                    });
                }
            }
            // Check for pending uploads
            if ((block.type === 'media' || block.type === 'file') && block.content?.files) {
                const files = block.content.files as any[];
                if (files.some(f => f.status === 'uploading' || f.status === 'pending')) {
                    issues.push({
                        network: "Загрузка",
                        message: "Дождитесь окончания загрузки всех файлов.",
                        type: 'error'
                    });
                }
                if (files.some(f => f.status === 'error')) {
                    issues.push({
                        network: "Загрузка",
                        message: "Есть файлы с ошибкой загрузки. Удалите их или попробуйте снова.",
                        type: 'error'
                    });
                }
            }
        });

        return issues;
    }, [validationIssues, postDoc.blocks]);

    const hasBlockingErrors = finalValidationIssues.some(i => i.type === 'error');
    const hasWarnings = finalValidationIssues.some(i => i.type === 'warning');

    const validConnectors = useMemo(() => {
        if (!selectedType) return [];
        return connectors.filter(conn => {
            const manifest = allManifests[conn.id];
            return manifest && manifest.manifest.supports.publicationTypes.includes(selectedType);
        });
    }, [connectors, allManifests, selectedType]);

    const totalAccounts = useMemo(() => {
        return validConnectors.reduce((acc, conn) => acc + (accountsMap[conn.id]?.length || 0), 0);
    }, [validConnectors, accountsMap]);

    const handleTypeSelect = async (type: PublicationType) => {
        setSelectedType(type);
        setPostDoc(prev => ({ ...prev, publicationType: type }));
        setSelectedAccountIds(new Set()); 
        setActiveConnectorId(null);
        setStep('account_selection');

        setAccountsLoading(true);
        try {
            const targetConnectors = connectors.filter(conn => {
                const manifest = allManifests[conn.id];
                return manifest && manifest.manifest.supports.publicationTypes.includes(type);
            });
            const newAccountsMap: Record<string, Account[]> = {};
            await Promise.all(targetConnectors.map(async (c) => {
                try {
                    const res = await getAccounts(c.id);
                    newAccountsMap[c.id] = res.result?.accounts || [];
                } catch(e) { 
                    newAccountsMap[c.id] = [];
                }
            }));
            setAccountsMap(newAccountsMap);
            const firstWithAccounts = targetConnectors.find(c => (newAccountsMap[c.id] || []).length > 0);
            if (firstWithAccounts) setActiveConnectorId(firstWithAccounts.id);
            else if (targetConnectors.length > 0) setActiveConnectorId(targetConnectors[0].id);
        } catch (e) {
            setError("Не удалось загрузить аккаунты");
        } finally {
            setAccountsLoading(false);
        }
    };

    const toggleAccount = (accId: string) => {
        const next = new Set(selectedAccountIds);
        if (next.has(accId)) next.delete(accId);
        else next.add(accId);
        setSelectedAccountIds(next);
    };

    const toggleAllInConnector = (connectorId: string) => {
        const accs = accountsMap[connectorId] || [];
        const next = new Set(selectedAccountIds);
        const allSelected = accs.every(a => next.has(a.id));
        if (allSelected) accs.forEach(a => next.delete(a.id));
        else accs.forEach(a => next.add(a.id));
        setSelectedAccountIds(next);
    };

    const toggleGlobalSelect = () => {
        const allAvailableAccounts: Account[] = [];
        validConnectors.forEach(vc => allAvailableAccounts.push(...(accountsMap[vc.id] || [])));
        if (allAvailableAccounts.length === 0) return;
        const isAllSelected = allAvailableAccounts.every(a => selectedAccountIds.has(a.id));
        if (isAllSelected) setSelectedAccountIds(new Set());
        else setSelectedAccountIds(new Set(allAvailableAccounts.map(a => a.id)));
    };

    const proceedToEditor = async () => {
        if (selectedAccountIds.size === 0) {
            setError("Выберите хотя бы один аккаунт");
            return;
        }
        setIsPublishing(true);
        try {
            const requiredConnectors = new Set<string>();
            Object.entries(accountsMap).forEach(([cid, accs]) => {
                (accs as Account[]).forEach(a => {
                    if (selectedAccountIds.has(a.id)) requiredConnectors.add(cid);
                });
            });
            const relevantManifests: ConnectorDetails[] = [];
            requiredConnectors.forEach(cid => {
                if (allManifests[cid]) {
                    const conn = connectors.find(c => cid === c.id);
                    relevantManifests.push({ ...allManifests[cid], logoUrl: conn?.logoUrl });
                }
            });
            if (relevantManifests.length === 0) throw new Error("Конфигурация не найдена");
            setLoadedManifests(relevantManifests);
            setCommonManifest(mergeManifests(relevantManifests.map(m => m.manifest), selectedType!));
            setStep('editor');
        } catch (e: any) {
            setError(e.message || "Ошибка загрузки конфигурации");
        } finally {
            setIsPublishing(false);
        }
    };

    const handleOpenSchedule = () => {
        setIsPublishDropdownOpen(false);
        setScheduleError(null);
        const now = new Date();
        now.setMinutes(Math.ceil((now.getMinutes() + 10) / 5) * 5); 
        now.setSeconds(0);
        now.setMilliseconds(0);
        const tzOffset = now.getTimezoneOffset() * 60000;
        setScheduleInputValue((new Date(now.getTime() - tzOffset)).toISOString().slice(0, 16));
        setShowScheduleModal(true);
    };

    const confirmSchedule = () => {
        if (!scheduleInputValue) return;
        const selectedDate = new Date(scheduleInputValue);
        if (selectedDate.getTime() <= Date.now()) {
            setScheduleError("Нельзя запланировать публикацию в прошлом. Пожалуйста, выберите время позже текущего.");
            return;
        }
        setScheduleError(null);
        setScheduledAt(selectedDate.toISOString());
        setShowScheduleModal(false);
        handlePublish(selectedDate.toISOString());
    };

    const handlePublish = async (overrideScheduledAt?: string | null) => {
        const finalScheduledAt = overrideScheduledAt !== undefined ? overrideScheduledAt : scheduledAt;
        
        if (postDoc.blocks.length === 0) {
            setError("Публикация не может быть пустой");
            return;
        }
        if (hasBlockingErrors) {
            setShowValidationPopover(true);
            return;
        }

        setError(null);
        setStep('publishing');
        setIsPublishing(true);
        setLoadingMessage("Подготовка...");

        try {
            // 1. Group Targets by Compatible Blocks AND Text Formatting
            // We group requests so that networks receiving the same set of blocks AND same formatting requirements are batched together.
            
            const requestGroups = new Map<string, { blocks: ApiBlock[], targets: PostTarget[] }>();

            for (const accId of selectedAccountIds) {
                // Find connector ID for this account
                let connectorId: string | undefined;
                let manifest: any;

                for (const [cid, accs] of Object.entries(accountsMap)) {
                    if ((accs as Account[]).some(a => a.id === accId)) {
                        connectorId = cid;
                        manifest = allManifests[cid]?.manifest;
                        break;
                    }
                }

                if (!connectorId || !manifest) continue;

                // CRITICAL: Get ONLY compliant blocks for this specific network.
                // This essentially "strips" blocks that generated warnings (ignoredBlocks) for this network.
                const { validBlocks } = getCompliantBlocks(
                    postDoc.blocks,
                    manifest,
                    postDoc.publicationType,
                    connectorId
                );

                if (validBlocks.length === 0) continue; // Skip if no blocks are valid for this network

                // Check text formatting support
                const supportedFormats = manifest.supports.textFormatting || [];
                const formattingMode = supportedFormats.includes('html') 
                    ? 'html' 
                    : (supportedFormats.includes('markdown') ? 'markdown' : 'plain');

                // Generate a signature for this set of blocks + formatting to group requests
                const signature = `${validBlocks.map(b => b.id).join('|')}::${formattingMode}`;

                if (!requestGroups.has(signature)) {
                    // Prepare API Blocks for this group using the VALID blocks only
                    const apiBlocks: ApiBlock[] = validBlocks.map(b => {
                        if (b.type === 'text') {
                            let textContent = b.content.text;

                            if (formattingMode === 'html') {
                                textContent = markdownToTelegramHtml(textContent);
                            } else if (formattingMode === 'plain') {
                                textContent = stripMarkdown(textContent);
                            }
                                
                            return { 
                                type: 'text', 
                                content: textContent, 
                                formatting: formattingMode 
                            };
                        }
                        if (b.type === 'media' || b.type === 'file') {
                            // Extract Server IDs from the new file structure
                            // The editor now stores files as objects with { serverId, status, ... }
                            const validFiles = (b.content.files || [])
                                .filter((f: any) => f.status === 'success' && f.serverId);
                            
                            if (validFiles.length === 0) return null;

                            return { 
                                type: b.type, 
                                items: validFiles.map((f: any) => ({ 
                                    type: (f.type.startsWith('video') || f.type === 'video') ? 'video' : 'photo', 
                                    source: f.serverId 
                                }))
                            };
                        }
                        if (b.type === 'poll') {
                            return { 
                                type: 'poll', 
                                question: b.content.question, 
                                options: b.content.options.filter((o: string) => o),
                                // Map Quiz fields
                                quiz: b.content.quiz,
                                correctOptionId: b.content.quiz ? b.content.correctOptionId : undefined,
                                explanation: b.content.quiz ? b.content.explanation : undefined
                            };
                        }
                        return null;
                    }).filter(b => b !== null) as ApiBlock[];

                    requestGroups.set(signature, { blocks: apiBlocks, targets: [] });
                }

                // Add target to the appropriate group
                requestGroups.get(signature)!.targets.push({
                    socialAccountId: accId,
                    publicationType: postDoc.publicationType,
                    modifiers: postDoc.networkSettings[connectorId] || {}
                });
            }

            // 2. Send Request (Single API Call with Variations)
            if (requestGroups.size > 0) {
                setLoadingMessage("Отправка в социальные сети...");
                
                const variations = Array.from(requestGroups.values()).map(group => ({
                    postDocument: { blocks: group.blocks },
                    targets: group.targets
                }));

                await sendPost({
                    variations,
                    scheduledAt: finalScheduledAt || undefined
                });
                
                setStep('moderation_success');
            } else {
                throw new Error("Не найдено подходящего контента для выбранных сетей.");
            }

        } catch (e: any) {
            setError(e.message || "Ошибка отправки");
            setStep('editor');
            setIsPublishing(false);
        }
    };

    if (step === 'publishing') return <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center animate-in fade-in"><Loader2 className="w-16 h-16 text-blue-600 animate-spin mb-6" /><h2 className="text-2xl font-bold text-slate-800">Публикуем...</h2><p className="text-slate-500 mt-2">{loadingMessage}</p></div>;
    if (step === 'moderation_success') return <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center p-6 text-center animate-in zoom-in-95"><div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6 shadow-sm"><ShieldCheck className="w-12 h-12 text-green-600" /></div><h2 className="text-3xl font-bold text-slate-800 mb-2">Готово!</h2><p className="text-slate-500 max-w-md mx-auto mb-8">Пост сформирован и будет опубликован после проверки модератором{scheduledAt ? ' в запланированное время' : ''}.</p><button onClick={onSuccess} className="px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-600/20 active:scale-95">На главную</button></div>;

    return (
        <div className="fixed inset-0 z-[100] bg-slate-50 flex flex-col animate-in slide-in-from-right duration-300 font-sans">
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shadow-sm z-50 flex-shrink-0">
                <div className="flex items-center gap-4">
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X className="w-6 h-6 text-slate-500" /></button>
                    <div className="hidden sm:block">
                        <h2 className="text-lg font-black text-slate-900">Создание публикации</h2>
                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                            <span className={step === 'type_selection' ? 'text-blue-600' : 'text-green-600'}>Тип</span>
                            <ArrowRight className="w-3 h-3" />
                            <span className={step === 'account_selection' ? 'text-blue-600' : (['editor', 'publishing'].includes(step) ? 'text-green-600' : '')}>Аккаунты</span>
                            <ArrowRight className="w-3 h-3" />
                            <span className={step === 'editor' ? 'text-blue-600' : ''}>Контент</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {step === 'account_selection' && (
                        <button onClick={proceedToEditor} disabled={selectedAccountIds.size === 0 || isPublishing} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all flex items-center shadow-lg shadow-blue-600/20 active:scale-95 disabled:opacity-50">
                            Далее <ArrowRight className="w-4 h-4 ml-2" />
                        </button>
                    )}
                    {step === 'editor' && (
                        <div className="flex items-center gap-3">
                             {(hasBlockingErrors || hasWarnings) && (
                                <div className="relative group/validation">
                                    <button onClick={() => setShowValidationPopover(!showValidationPopover)} className={`p-2.5 rounded-xl transition-all ${hasBlockingErrors ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600'}`}>
                                        <AlertTriangle className="w-5 h-5" />
                                    </button>
                                    <div className={`absolute right-0 top-full mt-3 w-80 bg-white border border-slate-200 rounded-2xl shadow-2xl z-[150] overflow-hidden transform transition-all origin-top-right ${showValidationPopover ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                                        <div className={`px-4 py-3 border-b font-black text-[10px] uppercase tracking-widest ${hasBlockingErrors ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
                                            {hasBlockingErrors ? 'Ошибки валидации' : 'Предупреждения'} ({finalValidationIssues.length})
                                        </div>
                                        <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                                            {finalValidationIssues.map((issue, idx) => (
                                                <div key={idx} className="p-3 rounded-xl hover:bg-slate-50 flex items-start gap-3 transition-colors">
                                                    <div className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${issue.type === 'error' ? 'bg-rose-500' : 'bg-amber-500'}`} />
                                                    <div className="text-xs">
                                                        <div className="font-black text-slate-900 mb-0.5">{issue.network}</div>
                                                        <div className="text-slate-500 leading-normal">{issue.message}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                             )}

                            <div className="relative" ref={dropdownRef}>
                                <button 
                                    onClick={() => setIsPublishDropdownOpen(!isPublishDropdownOpen)}
                                    disabled={isPublishing || hasBlockingErrors || postDoc.blocks.length === 0}
                                    className={`
                                        group flex items-center gap-3 px-6 py-3 bg-emerald-600 text-white font-black rounded-2xl
                                        transition-all duration-300 shadow-[0_4px_0_0_rgba(5,150,105,1)] active:shadow-none active:translate-y-[4px]
                                        hover:bg-emerald-500 disabled:opacity-50 disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none disabled:translate-y-0
                                    `}
                                >
                                    <Send className={`w-4 h-4 transition-transform ${isPublishDropdownOpen ? 'rotate-12 scale-110' : ''}`} />
                                    <span className="uppercase tracking-[0.1em] text-xs">Опубликовать</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform duration-500 ${isPublishDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isPublishDropdownOpen && (
                                    <div className="absolute right-0 top-full mt-5 w-72 bg-white border border-slate-200 rounded-[2.5rem] shadow-2xl z-[150] overflow-hidden animate-in fade-in slide-in-from-top-4 origin-top-right ring-8 ring-slate-100/40">
                                        <div className="p-3 space-y-1.5">
                                            <button 
                                                onClick={() => { setIsPublishDropdownOpen(false); handlePublish(null); }}
                                                className="w-full flex items-center gap-4 px-5 py-4 text-sm font-black text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-2xl transition-all group/item"
                                            >
                                                <div className="p-2.5 bg-emerald-100 text-emerald-600 rounded-xl group-hover/item:scale-110 transition-transform"><Send className="w-4 h-4" /></div>
                                                <span>Опубликовать сейчас</span>
                                            </button>
                                            <div className="h-px bg-slate-100 mx-4"></div>
                                            <button 
                                                onClick={handleOpenSchedule}
                                                className="w-full flex items-center gap-4 px-5 py-4 text-sm font-black text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-2xl transition-all group/item"
                                            >
                                                <div className="p-2.5 bg-blue-100 text-blue-600 rounded-xl group-hover/item:scale-110 transition-transform"><Clock className="w-4 h-4" /></div>
                                                <span>Запланировать пост</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {error && !showScheduleModal && (
                <div className="bg-rose-50 p-4 border-b border-rose-100 flex items-center justify-between text-rose-700 font-bold z-[40] animate-in slide-in-from-top-2">
                    <div className="flex items-center gap-3"><AlertCircle className="w-5 h-5" />{error}</div>
                    <button onClick={() => setError(null)} className="p-1.5 hover:bg-rose-100 rounded-lg"><X className="w-4 h-4" /></button>
                </div>
            )}

            {showScheduleModal && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 backdrop-blur-lg p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[3.5rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-200">
                        <div className="px-10 py-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                            <div className="flex items-center gap-5">
                                <div className="p-4 bg-blue-600 text-white rounded-[1.5rem] shadow-2xl shadow-blue-600/30"><Clock className="w-7 h-7" /></div>
                                <div>
                                    <h3 className="font-black text-2xl text-slate-900 leading-tight">Планирование</h3>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1">Выберите идеальный момент</p>
                                </div>
                            </div>
                            <button onClick={() => setShowScheduleModal(false)} className="p-2.5 bg-white border border-slate-100 rounded-2xl text-slate-300 hover:text-rose-500 transition-all hover:shadow-md"><X className="w-5 h-5" /></button>
                        </div>
                        
                        <div className="p-10">
                            {scheduleError && (
                                <div className="mb-6 bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-start gap-3 animate-in shake duration-500">
                                    <AlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs font-bold text-rose-700 leading-relaxed">{scheduleError}</p>
                                </div>
                            )}

                            <div className="space-y-8">
                                <div>
                                    <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4 ml-1">
                                        <CalendarIcon className="w-3.5 h-3.5 text-blue-500" />
                                        Дата и время отправки
                                    </label>
                                    <div className="relative group">
                                        <input 
                                            type="datetime-local" 
                                            step="300"
                                            min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                                            value={scheduleInputValue}
                                            onChange={(e) => { setScheduleInputValue(e.target.value); setScheduleError(null); }}
                                            className={`w-full px-8 py-7 bg-slate-50 border rounded-[2.5rem] outline-none transition-all text-slate-900 font-black text-2xl ${scheduleError ? 'border-rose-300 ring-8 ring-rose-500/5' : 'border-slate-200 focus:ring-8 focus:ring-blue-500/5 focus:border-blue-500 focus:bg-white shadow-inner'}`}
                                        />
                                    </div>
                                    
                                    <div className="mt-6 flex items-start gap-3 px-4 py-3 bg-blue-50/50 rounded-2xl border border-blue-100/50">
                                        <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                                        <p className="text-[11px] text-slate-500 font-medium leading-relaxed italic">
                                            Учитывайте, что пост должен пройти модерацию. Если проверка затянется, публикация выйдет с задержкой относительно выбранного времени.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-12 flex gap-4">
                                <button onClick={() => setShowScheduleModal(false)} className="flex-1 py-5 bg-slate-100 text-slate-500 font-black rounded-3xl hover:bg-slate-200 transition-all text-[11px] uppercase tracking-[0.2em]">Отмена</button>
                                <button onClick={confirmSchedule} disabled={!scheduleInputValue} className="flex-1 py-5 bg-blue-600 text-white font-black rounded-3xl hover:bg-blue-700 shadow-2xl shadow-blue-600/30 transition-all text-[11px] uppercase tracking-[0.2em] disabled:opacity-50">Подтвердить</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex-1 overflow-hidden relative flex flex-col">
                {step === 'type_selection' && (
                    <div className="h-full flex flex-col items-center justify-center p-8 overflow-y-auto animate-in fade-in">
                        <h2 className="text-3xl font-black text-slate-900 mb-10 tracking-tight">Что создаем сегодня?</h2>
                        {initialLoading ? (
                            <div className="grid md:grid-cols-3 gap-8 max-w-5xl w-full">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="bg-white p-10 rounded-[3rem] border border-slate-100 h-full min-h-[320px] shadow-sm flex flex-col animate-pulse">
                                        <div className="w-20 h-20 bg-slate-50 rounded-[2rem] mb-10" />
                                        <div className="h-8 w-2/3 bg-slate-50 rounded-full mb-4" />
                                        <div className="space-y-2 mt-auto">
                                            <div className="h-3 w-full bg-slate-50 rounded-full" />
                                            <div className="h-3 w-4/5 bg-slate-50 rounded-full" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid md:grid-cols-3 gap-8 max-w-5xl w-full">
                                {[
                                    { id: 'feed', label: 'Пост в ленту', icon: Newspaper, desc: 'Классическая публикация с текстом и галереей' },
                                    { id: 'story', label: 'История', icon: FileImage, desc: 'Вертикальный контент на 24 часа' },
                                    { id: 'reel', label: 'Клип / Reels', icon: Film, desc: 'Захватывающие короткие видео' }
                                ].map((type) => (
                                    <button key={type.id} onClick={() => handleTypeSelect(type.id as PublicationType)} className="group bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm hover:shadow-[0_20px_50px_rgba(37,99,235,0.1)] hover:border-blue-500 hover:-translate-y-3 transition-all text-left flex flex-col h-full">
                                        <div className="w-20 h-20 bg-blue-50 rounded-[2rem] flex items-center justify-center text-blue-600 mb-10 group-hover:bg-blue-600 group-hover:text-white transition-all group-hover:rotate-6 shadow-sm"><type.icon className="w-10 h-10" /></div>
                                        <h3 className="text-2xl font-black text-slate-900 mb-4">{type.label}</h3>
                                        <p className="text-slate-500 text-sm font-medium leading-relaxed opacity-80">{type.desc}</p>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {step === 'account_selection' && (
                    <div className="flex flex-col md:flex-row h-full animate-in fade-in">
                        <div className="w-full md:w-80 bg-slate-50/80 border-b md:border-b-0 md:border-r border-slate-200 flex flex-col flex-shrink-0">
                            <div className="p-5 border-b border-slate-200 bg-white">
                                <button onClick={toggleGlobalSelect} className={`w-full flex items-center justify-between p-5 rounded-3xl border transition-all ${totalAccounts > 0 && selectedAccountIds.size === totalAccounts ? 'bg-blue-600 border-blue-600 text-white shadow-xl shadow-blue-600/30' : 'bg-white border-slate-200 hover:border-blue-300'}`}>
                                    <div className="flex items-center gap-4"><div className={`w-6 h-6 rounded-xl border flex items-center justify-center ${totalAccounts > 0 && selectedAccountIds.size === totalAccounts ? 'bg-white text-blue-600' : 'bg-slate-50'}`}>{totalAccounts > 0 && selectedAccountIds.size === totalAccounts && <Check className="w-4 h-4" />}</div><span className="font-black text-xs uppercase tracking-widest">Выбрать всё</span></div>
                                    <div className="text-[10px] font-black px-2.5 py-1 bg-black/10 rounded-lg">{selectedAccountIds.size}/{totalAccounts}</div>
                                </button>
                            </div>
                            <div className="p-6 space-y-4 flex-1 overflow-y-auto custom-scrollbar">
                                {validConnectors.map(conn => {
                                    const accs = accountsMap[conn.id] || [];
                                    const selCount = accs.filter(a => selectedAccountIds.has(a.id)).length;
                                    const active = activeConnectorId === conn.id;
                                    return (
                                        <button key={conn.id} onClick={() => setActiveConnectorId(conn.id)} className={`relative flex items-center gap-4 px-5 py-4 rounded-[2rem] transition-all w-full border ${active ? 'bg-white shadow-xl border-blue-100 scale-[1.03]' : 'border-transparent text-slate-500 hover:bg-white/60'}`}>
                                            <div className="relative"><img src={conn.logoUrl} className={`w-12 h-12 rounded-2xl object-contain ${!active && 'grayscale opacity-60'}`} alt="" />{selCount > 0 && <div className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center border-4 border-white shadow-lg">{selCount}</div>}</div>
                                            <div className="text-left overflow-hidden"><div className={`font-black text-sm truncate ${active ? 'text-slate-900' : 'text-slate-600'}`}>{conn.displayName}</div><div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">{accs.length} аккаунтов</div></div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto bg-white p-10">
                            {activeConnectorId ? (
                                <div className="max-w-3xl mx-auto space-y-10">
                                    <div className="flex items-center justify-between pb-8 border-b border-slate-100">
                                        <div className="flex items-center gap-6"><div className="p-4 bg-slate-50 rounded-[1.5rem] border border-slate-100 shadow-sm"><img src={connectors.find(c => c.id === activeConnectorId)?.logoUrl} className="w-12 h-12 object-contain" alt="" /></div><div><h2 className="text-3xl font-black text-slate-900 tracking-tight">{connectors.find(c => c.id === activeConnectorId)?.displayName}</h2><p className="text-sm font-medium text-slate-400">Выберите аккаунты для публикации</p></div></div>
                                        <button onClick={() => toggleAllInConnector(activeConnectorId)} className="text-[10px] font-black uppercase tracking-widest text-blue-600 hover:bg-blue-50 px-5 py-2.5 rounded-2xl transition-all border border-blue-100">{(accountsMap[activeConnectorId]?.every(a => selectedAccountIds.has(a.id)) && accountsMap[activeConnectorId]?.length > 0) ? 'Сбросить' : 'Выбрать все'}</button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        {accountsMap[activeConnectorId]?.map(acc => {
                                            const sel = selectedAccountIds.has(acc.id);
                                            return (
                                                <div key={acc.id} onClick={() => toggleAccount(acc.id)} className={`group relative cursor-pointer p-6 rounded-[2.5rem] border-2 transition-all duration-300 ${sel ? 'border-blue-500 bg-blue-50/20 shadow-xl scale-[0.98]' : 'border-slate-50 hover:border-slate-200 hover:bg-slate-50'}`}>
                                                    <div className="flex items-center justify-between gap-5"><div className="flex items-center gap-5 overflow-hidden"><div className={`w-14 h-14 rounded-[1.25rem] flex items-center justify-center font-black text-xl transition-all ${sel ? 'bg-blue-600 text-white shadow-xl' : 'bg-slate-100 text-slate-400'}`}>{(acc.credentials.displayName?.[0] || "A").toUpperCase()}</div><div className="min-w-0"><div className={`font-black text-sm truncate tracking-tight mb-0.5 ${sel ? 'text-slate-900' : 'text-slate-700'}`}>{acc.credentials.displayName || acc.credentials.meta?.botUsername || "Account"}</div><div className="text-[10px] text-slate-400 font-mono opacity-60 truncate">ID: {acc.externalId}</div></div></div><div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${sel ? 'bg-blue-500 border-blue-500 scale-110 shadow-lg' : 'border-slate-200 bg-white group-hover:border-slate-300'}`}>{sel && <Check className="w-4 h-4 text-white" />}</div></div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : <div className="h-full flex flex-col items-center justify-center text-slate-200 space-y-6"><Filter className="w-32 h-32 opacity-10" /><p className="font-black text-xs uppercase tracking-[0.3em]">Выберите соцсеть слева</p></div>}
                        </div>
                    </div>
                )}

                {step === 'editor' && commonManifest && (
                    <UniversalEditor 
                        manifest={commonManifest}
                        connectors={loadedManifests}
                        document={postDoc}
                        onChange={setPostDoc}
                        onValidationChange={setValidationIssues}
                        accountsMap={accountsMap}
                        selectedAccountIds={selectedAccountIds}
                        onToggleAccount={toggleAccount}
                    />
                )}
            </div>
        </div>
    );
};